import React, { useState } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';
import { Search, ChevronDown, Calendar, X } from 'lucide-react';

import useHomepageSearchOptions from '../../../../hooks/useHomepageSearchOptions';
import { dayCountAvailableForSearch } from '../../../../config/configSearch';
import { buildListingSearchParams } from '../../../../util/buildListingSearchParams';
import { getStartOf, isInRange } from '../../../../util/dates';
import {
  Form,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  FieldDateRangePicker,
} from '../../../../components';

import css from './TopbarSearchForm.module.css';

const isSearchDateOutsideRange = day => {
  const start = getStartOf(new Date(), 'day');
  const end = getStartOf(start, 'day', null, dayCountAvailableForSearch, 'days');
  return !isInRange(day, start, end, 'day');
};

const topbarDateFormatOptions = { month: 'short', day: 'numeric' };

const TopbarSearchForm = props => {
  const { onSubmit, onDateRangeChange, initialValues, className } = props;
  const { categories, subcategoriesByCategory } = useHomepageSearchOptions({
    skipLocationFetch: true,
  });
  const [isCategoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [isSubcategoryMenuOpen, setSubcategoryMenuOpen] = useState(false);

  const handleFormSubmit = values => {
    const searchParams = buildListingSearchParams({
      target: 'searchPage',
      category: values.category,
      subcategory: values.subcategory,
      keyword: values.keywords,
      dateRange: values.dateRange,
    });

    onSubmit(searchParams);
  };

  const getCategoryLabel = categoryId => {
    if (!categoryId) {
      return 'Category';
    }
    return categories.find(c => c.value === categoryId)?.label || categoryId;
  };

  const getSubcategoryLabel = (categoryId, subcategoryId) => {
    if (!subcategoryId) {
      return 'Subcategory';
    }
    const options = subcategoriesByCategory[categoryId] || [];
    return options.find(s => s.value === subcategoryId)?.label || subcategoryId;
  };

  return (
    <FinalForm
      onSubmit={handleFormSubmit}
      initialValues={initialValues}
      render={formRenderProps => {
        const { handleSubmit, form, values } = formRenderProps;

        const handleCategoryChange = categoryId => {
          form.change('category', categoryId);
          form.change('subcategory', null);
        };

        const handleSubcategoryChange = subcategoryId => {
          form.change('subcategory', subcategoryId);
        };

        const subcategoryOptions = values.category
          ? subcategoriesByCategory[values.category] || []
          : [];

        return (
          <Form className={classNames(css.root, className)} onSubmit={handleSubmit}>
            <div className={css.searchPill}>
              <div className={classNames(css.section, css.filterSection)}>
                <Menu isOpen={isCategoryMenuOpen} onToggleActive={setCategoryMenuOpen}>
                  <MenuLabel className={css.menuLabel}>
                    <div className={css.labelContent}>
                      <span
                        className={classNames(css.labelText, {
                          [css.labelActive]: !!values.category,
                        })}
                      >
                        {getCategoryLabel(values.category)}
                      </span>
                      <ChevronDown size={14} className={css.chevron} />
                    </div>
                  </MenuLabel>
                  <MenuContent className={css.menuContent}>
                    <MenuItem key="all-categories">
                      <button
                        type="button"
                        className={css.menuItemButton}
                        onClick={() => {
                          handleCategoryChange(null);
                          setCategoryMenuOpen(false);
                        }}
                      >
                        All Categories
                      </button>
                    </MenuItem>
                    {categories.map(cat => (
                      <MenuItem key={cat.value}>
                        <button
                          type="button"
                          className={css.menuItemButton}
                          onClick={() => {
                            handleCategoryChange(cat.value);
                            setCategoryMenuOpen(false);
                          }}
                        >
                          {cat.label}
                        </button>
                      </MenuItem>
                    ))}
                  </MenuContent>
                </Menu>
              </div>

              <div className={css.separator} />

              <div className={classNames(css.section, css.filterSection)}>
                <Menu
                  isOpen={isSubcategoryMenuOpen}
                  onToggleActive={setSubcategoryMenuOpen}
                  isDisabled={!values.category}
                >
                  <MenuLabel className={css.menuLabel}>
                    <div className={css.labelContent}>
                      <span
                        className={classNames(css.labelText, {
                          [css.labelActive]: !!values.subcategory,
                        })}
                      >
                        {getSubcategoryLabel(values.category, values.subcategory)}
                      </span>
                      <ChevronDown size={14} className={css.chevron} />
                    </div>
                  </MenuLabel>
                  <MenuContent className={css.menuContent}>
                    <MenuItem key="all-subcategories">
                      <button
                        type="button"
                        className={css.menuItemButton}
                        onClick={() => {
                          handleSubcategoryChange(null);
                          setSubcategoryMenuOpen(false);
                        }}
                      >
                        All in {getCategoryLabel(values.category)}
                      </button>
                    </MenuItem>
                    {subcategoryOptions.map(sub => (
                      <MenuItem key={sub.value}>
                        <button
                          type="button"
                          className={css.menuItemButton}
                          onClick={() => {
                            handleSubcategoryChange(sub.value);
                            setSubcategoryMenuOpen(false);
                          }}
                        >
                          {sub.label}
                        </button>
                      </MenuItem>
                    ))}
                  </MenuContent>
                </Menu>
              </div>

              <div className={css.separator} />

              <div className={classNames(css.section, css.dateSection)}>
                <div className={css.dateWrapper}>
                  <Calendar size={16} className={css.dateIcon} />
                  <div className={css.datePickerRow}>
                    <FieldDateRangePicker
                      name="dateRange"
                      className={css.dateInput}
                      popupClassName={css.datePopup}
                      theme="light"
                      isDaily
                      isOutsideRange={isSearchDateOutsideRange}
                      startDateId="topbarSearchStartDate"
                      endDateId="topbarSearchEndDate"
                      startDatePlaceholderText="Date"
                      endDatePlaceholderText=""
                      dateFormatOptions={topbarDateFormatOptions}
                      showErrorMessage={false}
                      onChange={
                        onDateRangeChange
                          ? ({ startDate, endDate }) => {
                              // Only sync URL when the range is complete. Do not pass null while
                              // the user is re-selecting (first click resets to start-only) — that
                              // would clear the URL and remount the form, closing the calendar.
                              if (startDate && endDate) {
                                onDateRangeChange({ startDate, endDate });
                              }
                            }
                          : undefined
                      }
                    />
                    {values.dateRange?.startDate && (
                      <button
                        type="button"
                        className={css.clearDate}
                        onClick={() => {
                          form.change('dateRange', null);
                          if (onDateRangeChange) {
                            onDateRangeChange(null);
                          }
                        }}
                        aria-label="Clear date range"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className={css.separator} />

              <div className={classNames(css.section, css.keywordSection)}>
                <Field
                  name="keywords"
                  render={({ input }) => (
                    <input
                      {...input}
                      className={css.keywordInput}
                      placeholder="Keyword"
                      autoComplete="off"
                    />
                  )}
                />
              </div>

              <button type="submit" className={css.searchButton}>
                <Search size={18} />
              </button>
            </div>
          </Form>
        );
      }}
    />
  );
};

export default TopbarSearchForm;
